import { useState } from "react";
import type { CSSProperties } from "react";
import {
  ChevronDown,
  ChevronRight,
  History,
  ArrowRight,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import { callAnalyze } from "../../../lib/analyzeClient";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
import { montarContextoLaudoEspecial } from "../services/montarContextoLaudoEspecial";
import {
  derivarDelta,
  formatarDeltaTexto,
  DeltaAnalise,
} from "../services/deltaAnalise";
import { AnaliseIA } from "../types";

// B327 (AI_147) — PAINEL "O que mudou desde a última análise".
// Caminho A (determinístico): delta dos dados (mudanças de valor, novas consultas,
// novos exames/marcadores). Caminho B (B327-B): botão médico-only que pede à IA a
// INTERPRETAÇÃO do delta — etapa isolada `delta-interpretacao`, contexto clínico
// completo + análise anterior + delta (fatos exatos). Carrega sob demanda.
export function PainelDelta({
  paciente,
  analiseAnterior,
}: {
  paciente: Paciente;
  analiseAnterior: AnaliseIA;
}) {
  const { usuarioLogado } = useAuth();
  const souMedico = ehMedico(usuarioLogado);

  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [dados, setDados] = useState<any>(null);
  const [delta, setDelta] = useState<DeltaAnalise | null>(null);
  const [erro, setErro] = useState(false);

  // Interpretação por IA (B327-B)
  const [interpretando, setInterpretando] = useState(false);
  const [interpretacao, setInterpretacao] = useState<string | null>(null);
  const [erroIA, setErroIA] = useState(false);

  const abrir = async () => {
    if (aberto) {
      setAberto(false);
      return;
    }
    setAberto(true);
    if (delta !== null || carregando) return;
    setCarregando(true);
    setErro(false);
    try {
      const d = await coletarDadosCompletos(paciente);
      setDados(d);
      setDelta(derivarDelta(d, analiseAnterior.dataAnalise));
    } catch {
      setErro(true);
    } finally {
      setCarregando(false);
    }
  };

  const interpretar = async () => {
    if (!dados || !delta || interpretando) return;
    setInterpretando(true);
    setErroIA(false);
    try {
      const contextoClinico = montarContextoLaudoEspecial(dados);
      const deltaTexto = formatarDeltaTexto(delta);
      const achados = analiseAnterior.resumo?.principaisAchados || [];
      const baseline = [
        `Status geral: ${analiseAnterior.resumo?.statusGeral || "não informado"}`,
        achados.length ? `Principais achados:` : "",
        ...achados.map((a) => `- ${a}`),
      ]
        .filter(Boolean)
        .join("\n");

      const resp = await callAnalyze<{ interpretacao?: string; erro?: string }>({
        etapa: "delta-interpretacao",
        contextoClinico,
        deltaTexto,
        analiseAnterior: baseline,
      });
      if (resp.interpretacao) setInterpretacao(resp.interpretacao);
      else setErroIA(true);
    } catch {
      setErroIA(true);
    } finally {
      setInterpretando(false);
    }
  };

  const mudancas = delta
    ? [...delta.mudancasLab, ...delta.mudancasMarcadores]
    : [];
  const novosResumo: string[] = [];
  if (delta && delta.novosExamesLab > 0)
    novosResumo.push(`${delta.novosExamesLab} novo(s) exame(s) de laboratório`);
  if (delta && delta.novosMarcadores > 0)
    novosResumo.push(`${delta.novosMarcadores} nova(s) coleta(s) de marcadores`);

  return (
    <div
      style={{
        border: `1px solid ${theme.gray[200]}`,
        borderRadius: theme.borderRadius.lg,
        background: theme.gray[50],
        overflow: "hidden",
      }}
    >
      <button
        onClick={abrir}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 16px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontFamily: theme.fonts.sans,
          fontSize: "14px",
          fontWeight: 600,
          color: theme.gray[700],
          textAlign: "left",
        }}
      >
        {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <History size={16} color={theme.primary[600]} />
        O que mudou desde a análise anterior
      </button>

      {aberto && (
        <div style={{ padding: "0 16px 16px 16px" }}>
          {carregando && (
            <div style={statusLinha}>
              <Loader2 size={14} /> Comparando com a análise anterior…
            </div>
          )}

          {erro && (
            <p style={statusTexto}>
              Não foi possível comparar agora. Tente reabrir.
            </p>
          )}

          {!carregando && !erro && delta && (
            <>
              {delta.dataReferencia && (
                <p
                  style={{
                    margin: "0 0 10px 0",
                    fontFamily: theme.fonts.sans,
                    fontSize: "12px",
                    color: theme.gray[500],
                  }}
                >
                  Comparado à análise de <strong>{delta.dataReferencia}</strong>.
                </p>
              )}

              {delta.vazio && (
                <p style={{ ...statusTexto, margin: 0 }}>
                  Nada novo nos dados estruturados desde a análise anterior.
                </p>
              )}

              {mudancas.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={rotuloSecao}>Mudanças de valor</div>
                  {mudancas.map((m, i) => (
                    <div key={i} style={linhaMudanca}>
                      <strong style={{ fontWeight: 600 }}>{m.rotulo}:</strong>
                      <span style={{ color: theme.gray[500] }}>{m.de}</span>
                      <ArrowRight size={13} color={theme.gray[400]} />
                      <span style={{ fontWeight: 600 }}>{m.para}</span>
                    </div>
                  ))}
                </div>
              )}

              {delta.consultasNovas.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={rotuloSecao}>Novas consultas</div>
                  {delta.consultasNovas.map((c, i) => (
                    <div
                      key={i}
                      style={linhaConsultaResumo}
                      title={c.resumo}
                    >
                      {c.data && (
                        <strong style={{ fontWeight: 600, marginRight: "6px" }}>
                          {c.data}
                        </strong>
                      )}
                      {c.resumo}
                    </div>
                  ))}
                </div>
              )}

              {novosResumo.length > 0 && (
                <div>
                  <div style={rotuloSecao}>Novos dados</div>
                  <p style={{ ...linhaConsulta, margin: 0 }}>
                    {novosResumo.join(" · ")}
                  </p>
                </div>
              )}

              {/* B327-B — interpretação por IA (médico-only, só se há delta) */}
              {souMedico && !delta.vazio && !interpretacao && (
                <button
                  onClick={interpretar}
                  disabled={interpretando}
                  style={{
                    marginTop: "14px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    background: interpretando ? theme.gray[300] : theme.primary[600],
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    cursor: interpretando ? "default" : "pointer",
                    fontFamily: theme.fonts.sans,
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {interpretando ? (
                    <>
                      <Loader2 size={14} /> Interpretando as mudanças…
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} /> Interpretar as mudanças com a IA
                    </>
                  )}
                </button>
              )}

              {erroIA && (
                <p style={{ ...statusTexto, marginTop: "10px" }}>
                  Não foi possível interpretar agora. Tente novamente.
                </p>
              )}

              {interpretacao && (
                <div
                  style={{
                    marginTop: "14px",
                    padding: "12px 16px",
                    background: "white",
                    border: `1px solid ${theme.primary[200]}`,
                    borderRadius: theme.borderRadius.md,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                      fontSize: "12px",
                      fontWeight: 700,
                      color: theme.primary[700],
                    }}
                  >
                    <Sparkles size={14} /> Interpretação da IA
                  </div>
                  <TextoAnaliseMarkdown texto={interpretacao} />
                  <p
                    style={{
                      marginTop: "8px",
                      marginBottom: 0,
                      fontFamily: theme.fonts.sans,
                      fontSize: "11px",
                      color: theme.gray[400],
                      fontStyle: "italic",
                    }}
                  >
                    Apoio à decisão — revise antes de qualquer conduta.
                  </p>
                </div>
              )}

              {!delta.vazio && !interpretacao && (
                <p
                  style={{
                    marginTop: "12px",
                    marginBottom: 0,
                    fontFamily: theme.fonts.sans,
                    fontSize: "11px",
                    color: theme.gray[400],
                    fontStyle: "italic",
                  }}
                >
                  Delta determinístico dos dados. Use o botão para a leitura
                  interpretativa da IA.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const statusLinha: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: theme.gray[500],
  fontFamily: theme.fonts.sans,
  fontSize: "13px",
  padding: "8px 0",
};

const statusTexto: CSSProperties = {
  color: theme.gray[500],
  fontFamily: theme.fonts.sans,
  fontSize: "13px",
};

const rotuloSecao: CSSProperties = {
  fontFamily: theme.fonts.sans,
  fontSize: "12px",
  fontWeight: 700,
  color: theme.gray[800],
  marginBottom: "4px",
};

const linhaMudanca: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontFamily: theme.fonts.sans,
  fontSize: "13px",
  color: theme.gray[700],
  padding: "2px 0",
};

const linhaConsulta: CSSProperties = {
  fontFamily: theme.fonts.sans,
  fontSize: "13px",
  color: theme.gray[700],
  padding: "2px 0",
};

// B327 polido (AI_148): a linha da consulta exibia o resumo INTEIRO (um
// queixaPrincipal de parágrafo despejava tudo no painel). Clamp de 2 linhas via
// CSS — não altera o texto (a IA usa o resumo integral); o `title` mostra o
// completo no hover.
const linhaConsultaResumo: CSSProperties = {
  ...linhaConsulta,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
